import * as React from 'react';
import { theme } from 'svenssons/shared/theme';
import { styled, type StyledDecorator } from '@glitz/react';
import { useMatchDevice } from '@avensia/excite';
import { createMinLargeViewportDecorator } from 'webapp/viewport';
import { useViewEventTrigger } from 'webapp/tracking';
import { addViewItemEventToDataLayer, ListType } from 'webapp/tracking/events/items';
import { Carousel, useCarousel, type DeviceSettingType } from 'webapp/components/Carousel';
import { LoadingProductCard, ProductCard } from 'webapp/components/ProductCard';
import { sharedSpacing } from 'webapp/shared/sharedTheme';

type PropType = {
  id: string;
  name: string;
  products: NordicNest.Api.PartialProductApiModel[];
  slidePadding?: StyledDecorator;
};

const visibleCount: DeviceSettingType = { mobile: 2, tablet: 3, desktop: 4 };

export default styled(function ProductListCarousel(props: PropType) {
  const deviceMatch = useMatchDevice();
  const isSmallDevice = deviceMatch.isMobile || deviceMatch.isTablet;

  const carousel = useCarousel(props.id, {
    containScroll: 'keepSnaps',
    align: isSmallDevice ? 'start' : 'center',
  });

  const self = React.createRef<HTMLDivElement>();
  useViewEventTrigger(
    self,
    addViewItemEventToDataLayer,
    {
      products: props.products,
      list_id: props.id,
      list_name: props.name,
      list_type: ListType.ProductListBlock,
      list_pagination_index: 1,
      list_pagination_max: 1,
    },
    props.products.map(x => x.sku).join(','),
  );

  const nextSlideFactor: DeviceSettingType = React.useMemo(
    () => ({
      mobile: props.products.length > visibleCount.mobile ? 1.2 : 1,
      tablet: props.products.length > visibleCount.tablet ? 1.2 : 1,
      desktop: 1,
    }),
    [props.products.length],
  );

  return (
    <Base ref={self}>
      <Carousel
        visibleCount={visibleCount}
        gap={sharedSpacing.xs}
        showNextSlideFactor={nextSlideFactor}
        useTotalCount
        arrowNavigation={!isSmallDevice}
        arrowPosition={-theme.spacing.xxl}
        {...carousel}
      >
        {props.products?.map((product, index) => (
          <ProductCard
            list_id={props.id}
            list_name={props.name}
            list_type={ListType.ProductListBlock}
            key={product.url}
            product={product}
            list_pagination_index={1}
            list_pagination_max={1}
            index={index + 1}
          />
        ))}
      </Carousel>
    </Base>
  );
});

const nextSlideFactorLoadingList: DeviceSettingType = {
  mobile: 1.2,
  tablet: 1.2,
  desktop: 1,
};

export function LoadingProductListCarousel() {
  const carousel = useCarousel();

  return (
    <Base>
      <Carousel
        visibleCount={visibleCount}
        gap={sharedSpacing.xs}
        showNextSlideFactor={nextSlideFactorLoadingList}
        {...carousel}
      >
        {new Array(visibleCount.desktop).fill(1).map(({}, i) => (
          <LoadingProductCard key={i} />
        ))}
      </Carousel>
    </Base>
  );
}

const Base = styled.div(
  createMinLargeViewportDecorator({
    padding: { x: t => t.spacing.xxl },
  }),
);
